import type { ComponentPropsWithoutRef } from 'react'
import { useMemo } from 'react'
import { Badge } from '@/components/admin/shared/badge'
import { ComplexityAnalysis } from '@/components/ui/complexity-analysis'
import { Copyright } from '@/components/ui/copyright'
import { HighlightedCodeBlock } from '@/components/ui/highlighted-code-block'
import { SyntaxHighlightedMarkdown } from '@/components/ui/syntax-highlighted-markdown'
import type { EditorialApproachMeta } from './problem-context'
import { useProblem } from './problem-context'

const SOLUTION_TYPE_LABELS: Record<string, string> = {
  brute_force: 'Brute Force',
  acceptable: 'Acceptable',
  optimal: 'Optimal',
}

/** Parse an h2's text content to extract the approach number and optional name */
function parseApproachHeading(
  children: React.ReactNode,
): { number: number; name: string | null } | null {
  const text = typeof children === 'string' ? children : String(children)
  // Matches "1. Name" or "1."
  const numbered = text.match(/^(\d+)\.\s*(.*)$/)
  if (numbered)
    return {
      number: Number(numbered[1]),
      name: numbered[2]?.trim() || null,
    }
  // Matches "Approach 1"
  const named = text.match(/^Approach\s+(\d+)/)
  if (named) return { number: Number(named[1]), name: null }
  return null
}

function ApproachBadges({ approach }: { approach: EditorialApproachMeta }) {
  const hasBadges =
    approach.solutionType ||
    approach.difficulty ||
    (approach.strategies && approach.strategies.length > 0)

  if (!hasBadges) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {approach.strategies?.map((s) => (
        <Badge key={s} variant="topic">
          {s}
        </Badge>
      ))}
      {approach.difficulty && (
        <Badge variant={approach.difficulty}>{approach.difficulty}</Badge>
      )}
      {approach.solutionType && SOLUTION_TYPE_LABELS[approach.solutionType] && (
        <Badge variant={approach.solutionType}>
          {SOLUTION_TYPE_LABELS[approach.solutionType]}
        </Badge>
      )}
    </div>
  )
}

export function EditorialPane() {
  const { data, sections, problemSlug } = useProblem()

  const editorial = data?.editorial
  const approaches = editorial?.approaches ?? []

  // Build react-markdown component overrides to match admin preview styling
  const markdownComponents = useMemo(() => {
    if (approaches.length === 0) return undefined

    // Track current approach index across h2/h3 overrides within a render pass
    let currentApproach = -1

    return {
      // Approach headings: number pill + name + badges (matches ApproachPreview)
      h2: ({ children, ...props }: ComponentPropsWithoutRef<'h2'>) => {
        const heading = parseApproachHeading(children)
        if (!heading) return <h2 {...props}>{children}</h2>

        currentApproach = heading.number - 1
        const meta = approaches[currentApproach]

        return (
          <div className="not-prose mb-6 flex min-h-[2.5rem] flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex shrink-0 items-center gap-3">
              <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
                {heading.number}
              </span>
              {heading.name && (
                <span className="text-foreground whitespace-nowrap text-2xl font-semibold leading-none tracking-tight">
                  {heading.name}
                </span>
              )}
            </div>
            {meta && <ApproachBadges approach={meta} />}
          </div>
        )
      },

      // Implementation heading: gradient bar + code + complexity from approach data
      h3: ({ children, ...props }: ComponentPropsWithoutRef<'h3'>) => {
        const text = typeof children === 'string' ? children : String(children)

        if (text === 'Implementation') {
          const meta =
            currentApproach >= 0 ? approaches[currentApproach] : undefined
          return (
            <div className="not-prose mb-6">
              <div className="mb-3 flex h-6 items-center gap-2">
                <div className="from-sky-500 to-blue-500 h-5 w-1 shrink-0 rounded-full bg-gradient-to-b" />
                <span className="text-foreground text-base font-semibold leading-none tracking-wide">
                  Implementation
                </span>
              </div>
              {meta?.code && (
                <HighlightedCodeBlock code={meta.code} language="python" />
              )}
              {meta?.complexities && meta.complexities.length > 0 && (
                <ComplexityAnalysis
                  complexities={meta.complexities}
                  className="mt-4"
                />
              )}
            </div>
          )
        }

        // Skip standalone "Complexity" headings (rendered inline with code above)
        if (text === 'Complexity') {
          return null
        }

        return <h3 {...props}>{children}</h3>
      },

      // Styled horizontal rules matching admin preview
      hr: () => <hr className="not-prose border-border my-8" />,
    }
  }, [approaches])

  if (sections.editorial.status === 'loading') {
    return (
      <div className="p-4">
        <p className="text-muted-foreground text-center">
          Loading editorial content...
        </p>
      </div>
    )
  }

  if (sections.editorial.status === 'error') {
    return (
      <div className="p-4">
        <p className="text-center text-red-500">{sections.editorial.message}</p>
      </div>
    )
  }

  if (!problemSlug) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground text-center">No problem selected</p>
      </div>
    )
  }

  if (!editorial?.markdown) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground text-center">
          No solution has been written for this problem yet. Check back later!
        </p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-8 px-6 py-4 [&_img]:max-w-full">
        <SyntaxHighlightedMarkdown components={markdownComponents}>
          {editorial.markdown}
        </SyntaxHighlightedMarkdown>

        <Copyright scope="all" />
      </div>
    </div>
  )
}
